/**
 * Pending-purchase tracking for Meta (Facebook) measurement.
 *
 * When a checkout attempt starts we mint one record: a shared `eventId` plus the
 * real plan/value/currency of the selected option. The `eventId` travels two
 * ways — into the checkout metadata (`meta_event_id`, read by the worker's
 * Conversions API relay) and into the browser Pixel Purchase event on the
 * success screen — so Events Manager deduplicates the two into one conversion.
 *
 * Persisted in sessionStorage (not memory) because 3DS / hosted-page checkouts
 * redirect away and back; the success screen must still find the record.
 */

const STORAGE_KEY = 'lunai_pending_purchase';

// Generous enough for any 3DS / hosted-page round-trip, short enough that an
// abandoned attempt can't label a later visit's events with stale plan data.
const MAX_AGE_MS = 60 * 60 * 1000;

export interface PendingPurchase {
  eventId: string;
  planId: string;
  value: number;
  currency: string;
  startedAt: number;
}

/**
 * Record a new purchase attempt (overwrites any previous one — latest attempt
 * wins). Returns the record so callers can reuse the event id immediately.
 */
export function beginPurchaseTracking(planId: string, value: number, currency: string): PendingPurchase {
  const pending: PendingPurchase = {
    eventId: generateEventId(),
    planId,
    value,
    currency,
    startedAt: Date.now(),
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // sessionStorage unavailable — the browser Purchase event will be skipped,
    // the server-side CAPI event still fires from checkout metadata.
  }

  return pending;
}

/**
 * Read the pending purchase without consuming it (e.g. AddToCart enrichment).
 */
export function getPendingPurchase(): PendingPurchase | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as PendingPurchase;
    if (!pending || !pending.eventId) return null;
    if (pending.startedAt && Date.now() - pending.startedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return pending;
  } catch {
    return null;
  }
}

/**
 * Read AND clear the pending purchase. The success screen consumes it so a
 * reload of #success can never fire a second Purchase event.
 */
export function consumePendingPurchase(): PendingPurchase | null {
  const pending = getPendingPurchase();
  if (pending) {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore — worst case the record expires with the tab session.
    }
  }
  return pending;
}

function generateEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return `pe_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}
